import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { useSwitchToOwningClient } from '../lib/clientLink.js';
import MicButton from '../components/MicButton.jsx';
import { useDraft } from '../lib/draft.js';

function formatDate(iso) {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso + 'T12:00:00Z') : new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function Questions() {
  const { slug } = useParams();
  const { activeId } = useAuth();
  const navigate = useNavigate();

  const [state,   setState]   = useState({ loading: true, error: null, row: null });
  const [answer,  setAnswer]  = useState('');
  const [busy,    setBusy]    = useState(false);
  const [banner,  setBanner]  = useState(null);
  const answerRef = useRef(null);
  const clearDraft = useDraft(state.row ? `questions.answer.${state.row.job_id}` : null, answer, setAnswer);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, error: null, row: null });
      if (!activeId || !slug) return;
      const { data: rows, error } = await supabase
        .from('v_portal_questions')
        .select('*')
        .eq('client_id', activeId)
        .eq('slug', slug)
        .limit(1);
      if (cancelled) return;
      if (error) { setState({ loading: false, error: error.message, row: null }); return; }
      const row = rows && rows[0];
      if (!row) { setState({ loading: false, error: 'Questions not found — they may have been answered already.', row: null, notFound: true }); return; }
      setState({ loading: false, error: null, row });
    })();
    return () => { cancelled = true; };
  }, [activeId, slug]);
  // Session 11: opened without ?client= under the wrong client -> switch to the job's client.
  useSwitchToOwningClient('v_portal_questions', slug, !!state.notFound);

  async function onSubmit(e) {
    e.preventDefault();
    if (!answer.trim()) { setBanner({ tone: 'err', text: 'Please answer before sending.' }); return; }
    setBusy(true);
    try {
      const { error } = await supabase.rpc('portal_answer', {
        p_job_id: state.row.job_id, p_answer: answer.trim(),
      });
      if (error) throw error;
      clearDraft();
      setBanner({ tone: 'ok', text: 'Thanks — we\'ll use these to draft the post.' });
      setTimeout(() => navigate('/'), 900);
    } catch (e) {
      setBanner({ tone: 'err', text: `Couldn't send: ${e.message || e}` });
    } finally {
      setBusy(false);
    }
  }

  if (state.loading) return <p className="lede">Loading the questions…</p>;
  if (state.error)   return (
    <div className="empty">
      <h2>Couldn't open the questions</h2>
      <p>{state.error}</p>
      <p><Link to="/">← Back to your inbox</Link></p>
    </div>
  );

  const r = state.row;
  const questions = Array.isArray(r.questions) ? r.questions : [];

  return (
    <>
      <div className="reader-topbar">
        <div>
          <h1>{r.title || slug}</h1>
          <p className="lede" style={{ margin: 0 }}>
            <Link to="/">← All items</Link>
            {r.publish_date && <> · post scheduled for {formatDate(r.publish_date)}</>}
          </p>
        </div>
      </div>

      {banner && <div className={`banner ${banner.tone === 'ok' ? 'banner-ok' : 'banner-err'}`}>{banner.text}</div>}

      <section className="review-section">
        <h2>A few things we need from you</h2>
        {questions.length === 0 ? (
          <p className="hint">No specific questions on file for this post — write whatever background helps us tell the story.</p>
        ) : (
          <ol className="q-list">
            {questions.map((q, i) => (
              <li key={i}>
                {typeof q === 'string' ? q : (q.q || q.question || JSON.stringify(q))}
              </li>
            ))}
          </ol>
        )}
      </section>

      <form onSubmit={onSubmit} className="review-section">
        <label htmlFor="answers-box"><strong>Your answers</strong></label>
        <p className="hint">One box for everything — number them if you like, or just answer in prose.</p>
        <textarea
          id="answers-box"
          ref={answerRef}
          className="review-textarea"
          rows={10}
          placeholder="Answer freely. We'll pull the relevant pieces into the draft."
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          disabled={busy}
        />
        <MicButton value={answer} onChange={setAnswer} inputRef={answerRef} disabled={busy} clientId={activeId} hint />
        <div className="review-actions">
          <button type="submit" className="btn btn-primary" disabled={busy || !answer.trim()}>
            {busy ? 'Sending…' : 'Send answers'}
          </button>
        </div>
      </form>
    </>
  );
}
